import { getAutomationServerMQTTToken } from '@/data/automation-server';
import { type Workspace } from '@/data/workspaces';
import { type TokenData } from '@/data/mqtt';
import { 
  type PipelineWithStats, 
  type WorkspaceTopologyResponse, 
  type WorkspaceGroup, 
  type AutomationsGroups,
  type Process,
} from '@/types';
import { type MqttClient } from 'mqtt';

/**
 * MQTT Topic Architecture
 * 
 * TWO types of connections with different mountpoints:
 * 
 * 1. SERVER CONNECTION (one per automation server)
 *    Mountpoint: /orgs/{org}/automation-servers/{server}
 *    Used for: workspace create/delete commands
 *    Topics (relative to mountpoint):
 *      - logs                    -> receives command results and logs
 *      - workspace/create        -> publish workspace create requests
 *      - workspace/delete        -> publish workspace delete requests
 * 
 * 2. WORKSPACE CONNECTIONS (one per workspace, using workspace tokens)
 *    Mountpoint: /orgs/{org}/automation-servers/{server}/c/{workspace}
 *    Used for: topology, processes, process operations
 *    Topics (relative to mountpoint):
 *      - /topology               -> workspace topology data
 *      - /topology/subscribe     -> request topology updates
 *      - /processes/list         -> list of processes
 *      - /processes/c/{id}/...   -> process operations
 */

// Types for workspace commands
export interface WorkspaceCommandRequest {
  "request-id": string;
  name: string;
  [key: string]: any;
}

export interface WorkspaceDeleteRequest {
  "request-id": string;
  id: string;
  name?: string;
}

export interface WorkspaceCommandResult {
  "request-id": string;
  success: boolean;
  message?: string;
  error?: string;
}

export interface WorkspaceCommandLogMessage {
  "request-id": string;
  time: string;
  level: string;
  message: string;
}

// Global state
let globalAutomationsState: AutomationsGroups = {
  all: [],
  processes: {},
  workspaces: {},
  isLoading: true,
};

let globalWorkspaces: WorkspaceGroup = {};
let globalPipelineStats: any[] = [];
let workspacesWithTopology: Set<string> = new Set();
let workspacesWithProcesses: Set<string> = new Set();

// Persistent connections - never cleaned up
const serverConnections: Map<string, MqttClient> = new Map();  // serverId -> client
const workspaceConnections: Map<string, MqttClient> = new Map();  // `${serverId}-${workspaceId}` -> client
const pendingServerConnections: Map<string, Promise<MqttClient>> = new Map();
const pendingWorkspaceConnections: Map<string, Promise<MqttClient>> = new Map();

class GlobalMQTTManager {
  private workspaceCommandListeners: Map<string, (result: WorkspaceCommandResult) => void> = new Map();
  private workspaceLogListeners: Map<string, (log: WorkspaceCommandLogMessage) => void> = new Map();
  private listeners: Set<() => void> = new Set();
  private connectionTimeout: NodeJS.Timeout | null = null;
  private timeoutFired: boolean = false;

  async connect(tokens: TokenData[]) {
    console.log('GlobalMQTTManager: Connecting with', tokens.length, 'workspace tokens');
    
    // Reset timeout fired flag for new connection
    this.timeoutFired = false;
    
    // Set loading timeout
    if (this.connectionTimeout) clearTimeout(this.connectionTimeout);
    this.connectionTimeout = setTimeout(() => {
      this.timeoutFired = true;
      if (globalAutomationsState.isLoading) {
        globalAutomationsState = { ...globalAutomationsState, isLoading: false };
        this.notifyListeners();
      }
    }, 10000);

    // Create workspace connections for topology/processes
    for (const token of tokens) {
      await this.getWorkspaceConnection(token);
    }

    // Create server connections for workspace commands (one per unique server)
    const serverIds = [...new Set(tokens.map(t => t.automation_server_id))];
    for (const serverId of serverIds) {
      await this.getServerConnection(serverId);
    }
  }

  // Get or create workspace connection (for topology/processes)
  private async getWorkspaceConnection(token: TokenData): Promise<MqttClient | null> {
    const key = `${token.automation_server_id}-${token.workspace_id}`;
    
    const existing = workspaceConnections.get(key);
    if (existing) return existing;

    const pending = pendingWorkspaceConnections.get(key);
    if (pending) return pending;

    const promise = this.createWorkspaceConnection(token);
    pendingWorkspaceConnections.set(key, promise);
    
    try {
      const client = await promise;
      workspaceConnections.set(key, client);
      pendingWorkspaceConnections.delete(key);
      return client;
    } catch (error) {
      pendingWorkspaceConnections.delete(key);
      console.error(`Failed to connect to workspace ${token.workspace_id}:`, error);
      return null;
    }
  }

  private async createWorkspaceConnection(token: TokenData): Promise<MqttClient> {
    const currentHost = window.location.hostname;
    const mqttHostname = currentHost.replace(/^[^.]+\./, 'mqtt.');
    const mqttHost = `wss://${mqttHostname}/mqtt`;

    console.log(`GlobalMQTTManager: Creating workspace connection for ${token.workspace_id}`);

    const mqtt = await import('mqtt');
    
    return new Promise((resolve, reject) => {
      const client = mqtt.default.connect(mqttHost, {
        clientId: `bitswan-ws-${token.workspace_id.substring(0, 8)}-${Math.random().toString(16).substring(2, 6)}`,
        clean: true,
        reconnectPeriod: 5000,
        connectTimeout: 30000,
        username: "bitswan-frontend",
        password: token.token,
      });

      client.on("connect", () => {
        console.log(`GlobalMQTTManager: Workspace ${token.workspace_id} connected`);
        // Subscribe to workspace topics (relative to workspace mountpoint)
        client.subscribe("/topology", { qos: 0 });
        client.subscribe("/processes/list", { qos: 0 });
        // Request initial topology
        client.publish("/topology/subscribe", JSON.stringify({ count: 1 }), { qos: 0 });
        resolve(client);
      });

      client.on("error", (err) => {
        console.error(`GlobalMQTTManager: Workspace ${token.workspace_id} error:`, err);
      });

      client.on("message", (topic, message) => {
        this.handleWorkspaceMessage(token.automation_server_id, token.workspace_id, topic, message);
      });

      setTimeout(() => {
        if (!client.connected) {
          reject(new Error(`Connection timeout for workspace ${token.workspace_id}`));
        }
      }, 30000);
    });
  }

  // Get or create server connection (for workspace commands)
  async getServerConnection(serverId: string): Promise<MqttClient | null> {
    const existing = serverConnections.get(serverId);
    if (existing) return existing;

    const pending = pendingServerConnections.get(serverId);
    if (pending) return pending;

    const promise = this.createServerConnection(serverId);
    pendingServerConnections.set(serverId, promise);
    
    try {
      const client = await promise;
      serverConnections.set(serverId, client);
      pendingServerConnections.delete(serverId);
      return client;
    } catch (error) {
      pendingServerConnections.delete(serverId);
      console.error(`Failed to connect to server ${serverId}:`, error);
      return null;
    }
  }

  private async createServerConnection(serverId: string): Promise<MqttClient> {
    const tokenData = await getAutomationServerMQTTToken(serverId);
    
    const currentHost = window.location.hostname;
    const mqttHostname = currentHost.replace(/^[^.]+\./, 'mqtt.');
    const mqttHost = `wss://${mqttHostname}/mqtt`;

    console.log(`GlobalMQTTManager: Creating server connection for ${serverId}`);

    const mqtt = await import('mqtt');
    
    return new Promise((resolve, reject) => {
      const client = mqtt.default.connect(mqttHost, {
        clientId: `bitswan-srv-${serverId.substring(0, 8)}-${Math.random().toString(16).substring(2, 6)}`,
        clean: true,
        reconnectPeriod: 5000,
        connectTimeout: 30000,
        username: `frontend-${serverId}`,
        password: tokenData.token,
      });

      client.on("connect", () => {
        console.log(`GlobalMQTTManager: Server ${serverId} connected`);
        // Subscribe to logs for workspace command results
        client.subscribe("logs", { qos: 1 });
        resolve(client);
      });

      client.on("error", (err) => {
        console.error(`GlobalMQTTManager: Server ${serverId} error:`, err);
      });

      client.on("message", (topic, message) => {
        this.handleServerMessage(serverId, topic, message);
      });

      setTimeout(() => {
        if (!client.connected) {
          reject(new Error(`Connection timeout for server ${serverId}`));
        }
      }, 30000);
    });
  }

  private handleWorkspaceMessage(serverId: string, workspaceId: string, topic: string, message: Buffer) {
    try {
      const data = JSON.parse(message.toString());

      if (topic === "/topology") {
        this.handleTopology(data, serverId, workspaceId);
      } else if (topic === "/processes/list") {
        this.handleProcesses(data.processes, serverId, workspaceId);
      }
    } catch (error) {
      console.error("GlobalMQTTManager: Failed to parse workspace message:", error);
    }
  }

  private handleServerMessage(serverId: string, topic: string, message: Buffer) {
    try {
      const data = JSON.parse(message.toString());

      if (topic === "logs") {
        // Result message (has success boolean)
        if (data["request-id"] && typeof data.success === "boolean") {
          this.workspaceCommandListeners.get(data["request-id"])?.(data);
        }
        // Log message (has time, level, message)
        else if (data["request-id"] && data.time && data.level && data.message) {
          this.workspaceLogListeners.get(data["request-id"])?.(data);
        }
      }
    } catch (error) {
      console.error("GlobalMQTTManager: Failed to parse server message:", error);
    }
  }

  private handleTopology(data: WorkspaceTopologyResponse, serverId: string, workspaceId: string) {
    const workspace = globalWorkspaces[workspaceId];
    if (!workspace) {
      return;
    }

    const pipelines = Object.entries(data.topology ?? {}).map(([_, value]) => {
      let vscodeLink: string | undefined;
      if (workspace.editor_url && value.properties["relative-path"]) {
        vscodeLink = workspace.editor_url + "?folder=/home/coder/workspace" + 
          `&payload=[["openFile","vscode-remote:///home/coder/workspace/${value.properties["relative-path"]}/main.ipynb"]]`;
      }
      return {
        _key: value.properties["container-id"],
        ...value,
        pipelineStat: globalPipelineStats?.filter((stat) =>
          value.properties["deployment-id"].startsWith(stat.deployment_id)
        ) || [],
        automationServerId: serverId,
        automationServerName: workspace.automation_server_name,
        workspaceId,
        workspaceName: workspace.name,
        vscodeLink,
      };
    });

    // Immutable update: replace the outer globalWorkspaces reference so
    // React consumers (which compare `workspaces` by reference in their
    // useMemo deps) see the change and recompute. Mutating
    // `globalWorkspaces[workspaceId] = ...` in place left the outer ref
    // unchanged, causing stale table renders on page refresh.
    globalWorkspaces = {
      ...globalWorkspaces,
      [workspaceId]: {
        ...workspace,
        pipelines,
        processes: globalWorkspaces[workspaceId]?.processes ?? {},
      },
    };

    workspacesWithTopology.add(workspaceId);
    this.updateState();
  }

  private handleProcesses(processes: Record<string, Process>, serverId: string, workspaceId: string) {
    if (!globalWorkspaces[workspaceId]) {
      return;
    }

    Object.values(processes).forEach(p => {
      p.automation_server_id = serverId;
      p.workspace_id = workspaceId;
    });

    globalWorkspaces = {
      ...globalWorkspaces,
      [workspaceId]: {
        ...globalWorkspaces[workspaceId],
        processes,
      },
    };
    workspacesWithProcesses.add(workspaceId);
    this.updateState();
  }

  private updateState() {
    const allPipelines: PipelineWithStats[] = [];
    const allProcesses: Record<string, Process> = {};
    const totalWorkspaceCount = Object.keys(globalWorkspaces).length;
    // Require BOTH topology and processes per workspace before declaring the
    // initial load complete. Previously a single set was incremented by either
    // message type, so topology-only arrivals could flip isLoading to false
    // while /processes/list was still pending — causing pages to briefly
    // render their empty state.
    const hasAllWorkspaceMessages =
      totalWorkspaceCount > 0 &&
      workspacesWithTopology.size >= totalWorkspaceCount &&
      workspacesWithProcesses.size >= totalWorkspaceCount;

    Object.values(globalWorkspaces).forEach(ws => {
      if (ws.pipelines) {
        allPipelines.push(...ws.pipelines);
      }
      if (ws.processes) {
        Object.assign(allProcesses, ws.processes);
      }
    });

    globalAutomationsState = {
      all: allPipelines,
      processes: allProcesses,
      workspaces: globalWorkspaces,
      isLoading: !hasAllWorkspaceMessages && !this.timeoutFired,
    };

    if (this.connectionTimeout && hasAllWorkspaceMessages) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    this.notifyListeners();
  }

  addListener(listener: () => void) { this.listeners.add(listener); }
  removeListener(listener: () => void) { this.listeners.delete(listener); }
  private notifyListeners() { this.listeners.forEach(l => l()); }
  getState(): AutomationsGroups { return { ...globalAutomationsState }; }

  // Process operations (use workspace connections)
  createProcess(name: string, workspaceId: string, serverId: string) {
    const id = crypto.randomUUID();
    const client = workspaceConnections.get(`${serverId}-${workspaceId}`);
    client?.publish(`/processes/c/${id}/create`, JSON.stringify({ name }), { qos: 1 });
    return id;
  }

  deleteProcess(id: string, workspaceId: string, serverId: string) {
    const client = workspaceConnections.get(`${serverId}-${workspaceId}`);
    client?.publish(`/processes/c/${id}/gitops-req`, JSON.stringify({ action: "delete" }), { qos: 0 });
  }

  async getProcessContent(id: string, workspaceId: string, serverId: string): Promise<string | null> {
    const client = workspaceConnections.get(`${serverId}-${workspaceId}`);
    if (!client) return null;
    
    return new Promise((resolve, reject) => {
      const topic = `/processes/c/${id}/contents`;
      const handler = (t: string, m: Buffer) => {
        if (t === topic) {
          client.unsubscribe(topic);
          client.removeListener("message", handler);
          resolve(JSON.parse(m.toString()).content);
        }
      };
      client.subscribe(topic, { qos: 1 });
      client.on("message", handler);
      client.publish(`/processes/c/${id}/gitops-req`, JSON.stringify({ action: "get" }), { qos: 0 });
      setTimeout(() => { client.unsubscribe(topic); client.removeListener("message", handler); reject(new Error("Timeout")); }, 10000);
    });
  }

  setProcessContent(id: string, content: string, workspaceId: string, serverId: string) {
    const client = workspaceConnections.get(`${serverId}-${workspaceId}`);
    client?.publish(`/processes/c/${id}/set`, JSON.stringify({ content }), { qos: 1 });
  }

  deleteProcessAttachment(id: string, fileName: string, workspaceId: string, serverId: string) {
    const client = workspaceConnections.get(`${serverId}-${workspaceId}`);
    client?.publish(`/processes/c/${id}/attachments/c/${fileName}/gitops-req`, JSON.stringify({ action: "delete" }), { qos: 0 });
  }

  async getProcessAttachment(id: string, fileName: string, workspaceId: string, serverId: string): Promise<Blob | null> {
    const client = workspaceConnections.get(`${serverId}-${workspaceId}`);
    if (!client) return null;
    
    return new Promise((resolve, reject) => {
      const topic = `/processes/c/${id}/attachments/c/${fileName}/contents`;
      const handler = (t: string, m: Buffer) => {
        if (t === topic) {
          client.unsubscribe(topic);
          client.removeListener("message", handler);
          resolve(new Blob([new Uint8Array(m)]));
        }
      };
      client.subscribe(topic, { qos: 1 });
      client.on("message", handler);
      client.publish(`/processes/c/${id}/attachments/c/${fileName}/gitops-req`, JSON.stringify({ action: "get" }), { qos: 0 });
      setTimeout(() => { client.unsubscribe(topic); client.removeListener("message", handler); reject(new Error("Timeout")); }, 10000);
    });
  }

  async setProcessAttachment(id: string, fileName: string, file: File, workspaceId: string, serverId: string): Promise<boolean> {
    const client = workspaceConnections.get(`${serverId}-${workspaceId}`);
    if (!client) return false;
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1] || reader.result;
        client.publish(`/processes/c/${id}/attachments/c/${fileName}/set`, JSON.stringify({ content: base64 }), { qos: 1 });
        resolve(true);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }

  // Workspace commands (use server connections)
  async createWorkspace(serverId: string, request: Omit<WorkspaceCommandRequest, "request-id">, onLog?: (log: WorkspaceCommandLogMessage) => void): Promise<WorkspaceCommandResult> {
    const client = await this.getServerConnection(serverId);
    if (!client) throw new Error("No connection");
    
    const requestId = `create-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.workspaceCommandListeners.delete(requestId);
        this.workspaceLogListeners.delete(requestId);
        reject(new Error("Timeout"));
      }, 5 * 60 * 1000);

      this.workspaceCommandListeners.set(requestId, (result) => {
        clearTimeout(timeout);
        this.workspaceCommandListeners.delete(requestId);
        this.workspaceLogListeners.delete(requestId);
        resolve(result);
      });

      if (onLog) this.workspaceLogListeners.set(requestId, onLog);

      client.publish("workspace/create", JSON.stringify({ ...request, "request-id": requestId }), { qos: 1 });
    });
  }

  async deleteWorkspace(serverId: string, request: Omit<WorkspaceDeleteRequest, "request-id">, onLog?: (log: WorkspaceCommandLogMessage) => void): Promise<WorkspaceCommandResult> {
    const client = await this.getServerConnection(serverId);
    if (!client) throw new Error("No connection");
    
    const requestId = `delete-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.workspaceCommandListeners.delete(requestId);
        this.workspaceLogListeners.delete(requestId);
        reject(new Error("Timeout"));
      }, 5 * 60 * 1000);

      this.workspaceCommandListeners.set(requestId, (result) => {
        clearTimeout(timeout);
        this.workspaceCommandListeners.delete(requestId);
        this.workspaceLogListeners.delete(requestId);
        
        // Remove deleted workspace from state (immutable update so React
        // consumers see the reference change).
        if (result.success && request.id) {
          const { [request.id]: _removed, ...rest } = globalWorkspaces;
          globalWorkspaces = rest;
          this.updateState();
        }
        
        resolve(result);
      });

      if (onLog) this.workspaceLogListeners.set(requestId, onLog);

      client.publish("workspace/delete", JSON.stringify({ ...request, "request-id": requestId }), { qos: 1 });
    });
  }

  isServerConnected(serverId: string): boolean {
    return serverConnections.get(serverId)?.connected ?? false;
  }
}

const globalMQTTManager = new GlobalMQTTManager();

export class MQTTService {
  private static instance: MQTTService;

  private constructor() {}

  static getInstance(): MQTTService {
    if (!MQTTService.instance) {
      MQTTService.instance = new MQTTService();
    }
    return MQTTService.instance;
  }

  async initialize(tokens: TokenData[], workspaces: Workspace[] = []) {
    console.log('MQTTService: Initializing with', tokens.length, 'tokens');
    
    globalWorkspaces = workspaces.reduce<WorkspaceGroup>((acc, workspace) => {
      const existing = globalWorkspaces[workspace.id];
      acc[workspace.id] = {
        ...workspace,
        pipelines: existing?.pipelines ?? [],
        processes: existing?.processes ?? {},
      };
      return acc;
    }, {});
    workspacesWithTopology = new Set();
    workspacesWithProcesses = new Set();

    await globalMQTTManager.connect(tokens);
  }

  getState() { return globalMQTTManager.getState(); }
  addListener(l: () => void) { globalMQTTManager.addListener(l); }
  removeListener(l: () => void) { globalMQTTManager.removeListener(l); }
  updateTokens(tokens: TokenData[]) { this.initialize(tokens); }
  updatePipelineStats(stats: any[]) { globalPipelineStats = stats; }

  createProcess(name: string, workspaceId: string, serverId: string) { return globalMQTTManager.createProcess(name, workspaceId, serverId); }
  deleteProcess(id: string, workspaceId: string, serverId: string) { return globalMQTTManager.deleteProcess(id, workspaceId, serverId); }
  getProcessContent(id: string, workspaceId: string, serverId: string) { return globalMQTTManager.getProcessContent(id, workspaceId, serverId); }
  setProcessContent(id: string, content: string, workspaceId: string, serverId: string) { return globalMQTTManager.setProcessContent(id, content, workspaceId, serverId); }
  deleteProcessAttachment(id: string, fileName: string, workspaceId: string, serverId: string) { return globalMQTTManager.deleteProcessAttachment(id, fileName, workspaceId, serverId); }
  getProcessAttachment(id: string, fileName: string, workspaceId: string, serverId: string) { return globalMQTTManager.getProcessAttachment(id, fileName, workspaceId, serverId); }
  setProcessAttachment(id: string, fileName: string, file: File, workspaceId: string, serverId: string) { return globalMQTTManager.setProcessAttachment(id, fileName, file, workspaceId, serverId); }

  createWorkspace(serverId: string, request: Omit<WorkspaceCommandRequest, "request-id">, onLog?: (log: WorkspaceCommandLogMessage) => void) {
    return globalMQTTManager.createWorkspace(serverId, request, onLog);
  }

  deleteWorkspace(serverId: string, request: Omit<WorkspaceDeleteRequest, "request-id">, onLog?: (log: WorkspaceCommandLogMessage) => void) {
    return globalMQTTManager.deleteWorkspace(serverId, request, onLog);
  }

  isServerConnected(serverId: string) { return globalMQTTManager.isServerConnected(serverId); }
  
  disconnect() {
    // Don't actually disconnect - connections are persistent
  }
}

export default MQTTService;
